import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import moment from 'moment'
import { getInitials } from 'sutra/utils/get-initials'

import type { AgentCitationPost } from 'features/ask-yogi/types'
import { isNullValueAlias } from 'src/constants/filter-values'

// Null-ish labels not in `isNullValueAlias`'s canonical set. These are
// product/catalog-y strings the BE sends in place of a real
// product_hierarchy — treat them as absent for display so the catalog
// link doesn't render against a non-navigable "product".
const EXTRA_NULL_ALIASES = new Set(['not applicable', 'not available'])

dayjs.extend(relativeTime)

const GENERIC_FEEDBACK_NAME = 'Customer feedback'

export interface VerbatimCardFields {
  reviewerName: string
  /** Null = no initials bubble. Only present for named reviewers on
   * non-customer-care, non-social posts; anonymous / social / CC posts
   * render no bubble. */
  initials: string | null
  sourceLabel: string | null
  /** Non-null only for customer-care (UVOC) posts: replaces the reviewer
   * name and "on {source}" in the header ("customer feedback via phone"). */
  customerCareLabel: string | null
  /** Product name from the post's `product_hierarchy`, when tagged. CC and
   * social posts can carry one too (though many don't) — if the field is
   * null the card just doesn't render the catalog link. */
  productName: string | null
  starCount: number | null
  /** Absolute `MM/DD/yyyy` — shown as the hover tooltip over `dateRelative`. */
  dateLabel: string | null
  /** "3 months ago"-style — the visible date text under the quote box. */
  dateRelative: string | null
}

/**
 * Pure derivation of the card's display fields from the citation API's
 * post shape. Every field degrades gracefully — a support-ticket record
 * with no score renders no star row, a record with no username renders
 * "Customer feedback" with no initials bubble — the card never has
 * nothing to show, and never falsely labels a social or support post as
 * a "reviewer".
 *
 * Social posts (is_social) swap in the user-facing platform name as the
 * source label and, when there's no named author, use "Post" / "Comment"
 * as the subject — "Post on Reddit", "Comment on TikTok".
 */
export function deriveVerbatimCardFields(
  post: AgentCitationPost | null | undefined,
): VerbatimCardFields {
  const username = stripNullAlias(post?.username)
  const rawSourceLabel = stripNullAlias(
    post?.source_type ?? post?.syndicated_source,
  )
  const score = stripNullAlias(post?.score != null ? String(post.score) : null)
  const isCustomerCare = post?.is_customer_care === true
  const isSocial = !isCustomerCare && post?.is_social === true
  const socialPlatform = isSocial ? stripNullAlias(post?.social_platform) : null
  const socialPostCategory = isSocial
    ? stripNullAlias(post?.social_post_category)
    : null
  const sourceLabel = socialPlatform ?? rawSourceLabel

  const nameInitials = username ? getInitials({ fullName: username }) : ''
  const socialFallbackName = socialPostCategory ?? 'Post'

  return {
    reviewerName:
      username ?? (isSocial ? socialFallbackName : GENERIC_FEEDBACK_NAME),
    initials:
      !isCustomerCare && !isSocial && nameInitials ? nameInitials : null,
    sourceLabel,
    customerCareLabel: isCustomerCare
      ? customerCareLabel(stripNullAlias(post?.channel))
      : null,
    productName: stripNullAlias(post?.product_hierarchy),
    starCount:
      score != null
        ? Math.max(0, Math.min(5, Math.round(Number(score))))
        : null,
    dateLabel:
      post?.create_time != null
        ? moment.unix(post.create_time).utc().format('MM/DD/yyyy')
        : null,
    dateRelative:
      post?.create_time != null ? dayjs.unix(post.create_time).fromNow() : null,
  }
}

function customerCareLabel(channel: string | null): string {
  const trimmed = channel?.trim()
  // Only the first letter is raised — connectors store acronyms ("SMS") that
  // lowercasing or title-casing the rest would mangle.
  return trimmed
    ? `Customer feedback via ${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`
    : 'Customer feedback'
}

/** Treat null, undefined, every FE null-alias (`'No Value'`, `'N/A'`,
 * `'Unknown'`, etc. — see `isNullValueAlias`), and the extra product-y
 * null labels above as absent. The BE sends several null-ish strings for
 * fields like `product_hierarchy`; a card that renders "Not Applicable"
 * as a catalog link is worse than one that renders no link at all. */
function stripNullAlias(value: string | null | undefined): string | null {
  if (value == null) return null
  if (isNullValueAlias(value)) return null
  if (EXTRA_NULL_ALIASES.has(value.trim().toLowerCase())) return null
  return value
}
